import { ScanTree } from "../../../cluetheory/scans/ScanTree";
import { Clues } from "../../../model/Clues";
import BoundsBuilder from "../../../../lib/gamemap/BoundsBuilder";
import { Path } from "../../../../lib/runescape/pathing";
import { TileCoordinates, TileRectangle } from "../../../../lib/runescape/coordinates";
import { Rectangle, Vector2 } from "../../../../lib/math";
import { TileArea } from "../../../../lib/runescape/coordinates/TileArea";
import { ScanRegionPolygon } from "../../ScanLayer";
import { PathStepEntity } from "../../../ui/map/entities/PathStepEntity";
import { Scans } from "../../../cluetheory/Scans";
import PulseButton from "./PulseButton";
import { util } from "../../../../lib/util/util";
import { ClueSolvingSubBehaviour } from "../../ClueSolvingSubBehaviour";
import { C } from "../../../../lib/ui/constructors";
import { TextRendering } from "../../../ui/TextRendering";
import { GameLayer } from "../../../../lib/gamemap/GameLayer";
import { ScanEditLayer } from "../../../ui/theorycrafting/scanedit/ScanEditor";
import { ScanCaptureService, ScanPanelOverlay } from "./ScanPanelReader";
import { ScanControlPrototype } from "./ScanInputBehaviour";
import { ScanMinimapOverlay } from "./ScanMinimapOverlay";
import { MinimapReader } from "../../../../lib/alt1/readers/MinimapReader";
import { ClueTrainerWiki } from "../../../wiki";
import { SettingsModal } from "../../../ui/settings/SettingsEdit";
import { drawTileArea } from "../../../overlay3d/PathRender";
import { SingleBehaviour } from "../../../../lib/ui/Behaviour";
import { SimpleGLOverlay } from "../../../overlay3d/SimpleGLOverlay";
import { MutableMesh } from "../../../overlay3d/meshes/MutableMesh";
import { PathOverlay3d } from "../../../overlay3d/PathOverlay3D";
import { Alt1 } from "../../../../lib/alt1/Alt1";
var AugmentedScanTree = ScanTree.Augmentation.AugmentedScanTree;
var cls = C.cls;
var Order = util.Order;
var spotNumber = ScanTree.spotNumber;
var digSpotArea = Clues.digSpotArea;
var hbox = C.hbox;
var spacer = C.spacer;
var inlineimg = C.inlineimg;
function findTripleNode(tree, spot) {
    function searchDown(node) {
        if (!node.remaining_candidates.some(c => TileCoordinates.eq(c, spot)))
            return null;
        for (const child of (node.children ?? [])) {
            const res = searchDown(child.value);
            if (res)
                return res;
        }
        return node;
    }
    function searchUp(node) {
        if (!node)
            return null;
        if (!node.remaining_candidates.some(c => TileCoordinates.eq(c, spot)))
            return searchUp(node.parent?.node);
        return searchDown(node);
    }
    return searchUp(tree);
}
class FloorDistinction {
    constructor(distinction) {
        this.distinction = distinction;
    }
    get(known_level, is_different_level) {
        if (known_level == undefined)
            return null;
        if (is_different_level == undefined)
            return null;
        const compatible_nodes = this.distinction.filter(e => {
            const node_expected_different_level = e.level != known_level;
            return is_different_level == node_expected_different_level;
        });
        // When exactly one node matches the current state of the "different level" state, select it.
        if (compatible_nodes.length == 1)
            return compatible_nodes[0].node;
        return null;
    }
}
function asFloorDistinctionNode(node) {
    if (node.depth != 0)
        return null;
    if (node.children.length != 2)
        return null;
    const a = node.children[0].value.remaining_candidates[0].level;
    const b = node.children[1].value.remaining_candidates[0].level;
    if (a == b)
        return null;
    if (!node.children[0].value.remaining_candidates.every(c => c.level == a))
        return null;
    if (!node.children[1].value.remaining_candidates.every(c => c.level == b))
        return null;
    return new FloorDistinction([
        { level: a, node: node.children[0].value },
        { level: b, node: node.children[1].value },
    ]);
}
export class ScanTreeSolving extends ClueSolvingSubBehaviour {
    constructor(parent, method, original_interface_capture, settings) {
        super("ScanTree", parent, "method");
        this.method = method;
        this.original_interface_capture = original_interface_capture;
        this.settings = settings;
        this.node = null;
        this.augmented = null;
        this.layer = null;
        this.overlay = this.withSub(new SingleBehaviour());
        this.region_overlay = this.withSub(new SingleBehaviour());
        this.handling_layer = null;
        this.augmented = ScanTree.Augmentation.basic_augmentation(method.method.tree, method.clue.clue);
        ScanTree.Augmentation.synthesize_triple_nodes(this.augmented);
        if (this.parent.app.in_alt1) {
            if (this.settings.value().show_minimap_overlay_scantree) {
                this.minimap_overlay = this.withSub(new ScanMinimapOverlay(MinimapReader.instance(), settings, "scantree").setRange(this.method.method.tree.assumed_range));
            }
            this.scan_panel_capture_service = new ScanCaptureService(this.original_interface_capture);
            this.scan_panel_overlay = this.withSub(new ScanPanelOverlay().connect(this.scan_panel_capture_service));
            this.scan_panel_overlay.enabled.bindTo(this.parent.app.settings.observable_settings.map(s => s.solving.scans.panel_status_overlay_enabled, this.lifetime_manager));
            this.scan_input_control = this.withSub(new ScanControlPrototype(this.scan_panel_capture_service));
            this.scan_input_control.visible.bindTo(this.parent.app.settings.observable_settings.map(s => s.solving.scans.input_control_enabled, this.lifetime_manager));
            this.scan_input_control.onNodeSelection(node => this.setNode(node));
        }
    }
    fit(active_path_section) {
        const node = this.node;
        const bounds = new BoundsBuilder();
        // 1. The path
        if (node.raw.path.length > 0) {
            bounds.addRectangle(Path.bounds(active_path_section, true));
        }
        else {
            if (node.region?.area) {
                bounds.addArea(node.region.area);
            }
        }
        bounds.fixLevel();
        bounds.setDistanceLimit(320);
        //2. If no children: All Candidates
        if (node.children.length == 0)
            node.remaining_candidates.forEach((c) => bounds.addTile(c));
        if (this.settings.value().zoom_behaviour_include_triples || node.children.every(c => c.key.pulse == 3)) {
            // Add triple spots
            node.children.filter(c => c.key.pulse == 3).flatMap(c => c.value.remaining_candidates)
                .forEach(s => bounds.addTile(s));
        }
        if (this.settings.value().zoom_behaviour_include_doubles) {
            // Add double spots
            node.children.filter(c => c.key.pulse == 2).flatMap(c => c.value.remaining_candidates)
                .forEach(s => bounds.addTile(s));
        }
        if (this.settings.value().zoom_behaviour_include_singles) {
            // Add single spots
            node.children.filter(c => c.key.pulse == 1).flatMap(c => c.value.remaining_candidates)
                .forEach(s => bounds.addTile(s));
        }
        this.parent.map_layer.fit(bounds.get());
    }
    renderLayer() {
        const node = this.node;
        this.layer.clearLayers();
        const pos = node.region
            ? TileArea.activate(node.region.area).center()
            : Path.ends_up(node.raw.path);
        if (pos) {
            this.parent.map_layer.getMap().floor.set(pos.level);
        }
        else {
            this.parent.map_layer.getMap().floor.set(Math.min(...node.remaining_candidates.map((c) => c.level)));
        }
        if (pos && node.remaining_candidates.length > 1
            && !(node.region && node.region.area && Rectangle.width(TileArea.toRect(node.region.area)) > this.method.method.tree.assumed_range * 2)) {
            this.parent.map_layer.scan_layer.marker.setFixedSpot(pos);
        }
        else {
            this.parent.map_layer.scan_layer.marker.setFixedSpot(null);
        }
        this.parent.map_layer.scan_layer.setActiveCandidates(node.remaining_candidates);
        new ScanRegionPolygon(ScanTree.getTargetRegion(node)).setOpacity(1).addTo(this.layer);
        AugmentedScanTree.collect_parents(node, false).forEach(n => {
            new ScanRegionPolygon(ScanTree.getTargetRegion(n)).setOpacity(0.5).addTo(this.layer);
            PathStepEntity.renderPath(n.raw.path).eachEntity(e => e.setOpacity(0.5)).addTo(this.layer);
        });
        const all_triple = node.children.every(c => c.key.pulse == 3);
        // Children paths are rendered with 0.5
        node.children
            .filter(c => all_triple || c.key.pulse != 3)
            .forEach(c => {
            PathStepEntity.renderPath(c.value.raw.path).eachEntity(l => l.setOpacity(0.5)).addTo(this.layer);
            new ScanRegionPolygon(ScanTree.getTargetRegion(c.value)).setOpacity(0.5).addTo(this.layer);
        });
    }
    /**
     * Update the interface to display the currently selected node in the decision tree.
     * @private
     */
    updateInterface() {
        const node = this.node;
        this.tree_widget.empty();
        let content = cls("ctr-neosolving-solution-row").appendTo(this.tree_widget);
        {
            const list = c("<ol class='breadcrumb' style='margin-bottom: unset'></ol>")
                .append(...AugmentedScanTree.collect_parents(node)
                .map(n => c("<li>").addClass("breadcrumb-item")
                .append(c("<span class='nisl-textlink'>")
                .on("click", () => this.setNode(n))
                .text(AugmentedScanTree.decision_string(n)))));
            const ui_nav = hbox(list, spacer(), inlineimg("/assets/icons/info_nis.png").css("height", "1em").css("margin-top", "2px").addClass("ctr-clickable")
                .on("click", () => ClueTrainerWiki.openOnPage("scantrees"))
                .tooltip("Learn more about scan trees."), inlineimg("/assets/icons/settings.png").addClass("ctr-clickable").css("height", "1em").css("margin-top", "2px").addClass("ctr-clickable")
                .on("click", async () => {
                await SettingsModal.openOnPage("scans");
            }));
            const last = list.container.children().last();
            last.text(last.children().first().text()).addClass("active");
            content.append(ui_nav);
        }
        if (node.raw?.path?.length > 0 || node.raw?.region?.name || node.children.length == 0) {
            content.append(cls('ctr-neosolving-nextscanstep')
                .append(...this.parent.app.template_resolver.with(...ScanTreeSolving.scan_tree_template_resolvers(node))
                .resolve(ScanTree.getInstruction(node))));
        }
        {
            const all_triples = node.children.every(e => e.key.pulse == 3);
            node.children
                .sort(Order.comap(Scans.Pulse.compare, (a) => a.key))
                .forEach((child) => {
                const resolvers = this.parent.app.template_resolver.with(...ScanTreeSolving.scan_tree_template_resolvers(child.value));
                cls("ctr-neosolving-scantreeline")
                    .addClass("ctr-clickable")
                    .on("click", () => {
                    this.setNode(child.value);
                })
                    .append(((child.key.pulse == 3 && child.key.spot && all_triples)
                    ? PulseButton.forSpot(spotNumber(node.root.raw, child.value.remaining_candidates[0]))
                    : PulseButton.forPulse(child.key, node.children.map(c => c.key)))
                    .onClick(() => {
                    this.setNode(child.value);
                }), (child.key.pulse != 3 || child.key.spot)
                    ? c().append(...resolvers.resolve(ScanTree.getInstruction(child.value)))
                    : C.italic("Select spot on map")).appendTo(content);
            });
        }
    }
    setNode(node) {
        if (node == this.node)
            return;
        this.node = node;
        if (this.scan_input_control)
            this.scan_input_control.setActiveNode(node);
        if (node.children.some(c => c.key.pulse != 3) || node.remaining_candidates.length <= 1)
            this.parent.map_layer.scan_layer.setSpotOrder(null);
        else
            this.parent.map_layer.scan_layer.setSpotOrder(this.method.method.tree.ordered_spots);
        this.registerSolution(TileArea.fromRect(TileRectangle.extend(TileRectangle.from(...node.remaining_candidates), 1)));
        this.updateInterface();
        this.renderLayer();
        // Setting the path in the path control will in turn trigger the section selected event.
        // This in turn triggers fitting the map, so we do not need to do that here explicitly.
        this.parent.path_control.reset().setPath(node.raw?.path ?? [], { method: this.method, node });
        if (Alt1.instance().featureGL()) {
            const is_triple_disambiguation = node.children.every(c => c.key.pulse == 3);
            {
                const region = ScanTree.getTargetRegion(node);
                (async () => {
                    const builder = new MutableMesh();
                    if (region && !is_triple_disambiguation && Vector2.max_axis(TileArea.size(region.area)) < 20) {
                        builder.add((await drawTileArea(region.area)).recolor([0, 255, 0, 50]));
                    }
                    for (let remainingCandidate of node.remaining_candidates) {
                        builder.add((await drawTileArea(digSpotArea(remainingCandidate))).recolor([100, 100, 100, 255]));
                    }
                    return builder.finalize();
                })().then(mesh => {
                    this.region_overlay.set(new SimpleGLOverlay(mesh));
                });
            }
            const overlay_relevant_nodes = [
                node,
                //...ScanTree.Augmentation.AugmentedScanTree.collect_parents(node),
                ...node.children.map(c => c.value) //.filter(n => is_triple_disambiguation || n.parent.key.pulse != 3)
            ];
            PathOverlay3d.forPaths(overlay_relevant_nodes.map(n => n.raw.path)).then(overlay => {
                if (this.node != node)
                    return; // Check that the node is still active to prevent races
                this.overlay.set(overlay);
            });
        }
    }
    begin() {
        this.parent.map_layer.scan_layer.setSpots(this.method.method.tree.ordered_spots);
        this.parent.map_layer.scan_layer.marker.setRadius(this.method.method.tree.assumed_range, this.method.method.assumptions.meerkats_active);
        this.tree_widget = c().appendTo(this.parent.map_layer.scantree_container);
        this.layer = new GameLayer().addTo(this.parent.map_layer.scan_layer);
        const self = this;
        this.parent.map_layer.scan_layer.marker.add(this.handling_layer = new class extends GameLayer {
            eventClick(event) {
                event.onPre(() => {
                    if (event.active_entity instanceof ScanEditLayer.SpotMarker) {
                        event.stopAllPropagation();
                        self.setNode(findTripleNode(self.node, event.active_entity.spot));
                        self.registerSolution(digSpotArea(event.active_entity.spot));
                    }
                });
            }
        });
        const findStartNode = () => {
            const root = this.augmented.root_node;
            const initial_is_dl = this.original_interface_capture?.isDifferentLevel();
            return asFloorDistinctionNode(root)?.get(this.parent.getAssumedPlayerPositionByLastClueSolution()?.origin?.level, initial_is_dl) ?? root;
        };
        this.setNode(findStartNode());
    }
    end() {
        this.tree_widget.remove();
        this.tree_widget = null;
        this.parent.map_layer.scan_layer.marker.setFixedSpot(null);
        this.parent.map_layer.scan_layer.marker.clearManualMarker();
        this.parent.map_layer.scan_layer.setSpotOrder(null);
        this.handling_layer?.remove();
        if (this.layer) {
            this.layer.remove();
            this.layer = null;
        }
    }
    onSectionSelectedInPathControl(path) {
        this.fit(path);
    }
}
(function (ScanTreeSolving) {
    var shorten_integer_list = util.shorten_integer_list;
    var render_digspot = TextRendering.render_digspot;
    var render_scanregion = TextRendering.render_scanregion;
    function scan_tree_template_resolvers(node) {
        return [
            {
                name: "target", apply: () => {
                    if (node.remaining_candidates.length == 1) {
                        return [{
                                type: "domelement",
                                value: render_digspot(spotNumber(node.root.raw, node.remaining_candidates[0]))
                            }];
                    }
                    else {
                        return [{
                                type: "domelement",
                                value: render_scanregion(node.raw.region?.name || "_")
                            }];
                    }
                }
            },
            {
                name: "candidates", apply: () => {
                    return [{
                            type: "domelement",
                            value: c(util.natural_join(shorten_integer_list(node.remaining_candidates.map(c => spotNumber(node.root.raw, c)), c => render_digspot(c).raw().outerHTML)))
                        }];
                }
            }
        ];
    }
    ScanTreeSolving.scan_tree_template_resolvers = scan_tree_template_resolvers;
})(ScanTreeSolving || (ScanTreeSolving = {}));
